import type { LapComparisonChannel } from "@/types";

export const REFERENCE_COLOR = "#60a5fa";
export const COMPARISON_COLOR = "#fb923c";
export const MAX_COMPARISON_CHANNELS = 6;
export const DEFAULT_CHANNEL_KEY = "speed";

/** Index of the grid position closest to `x` (positions are ascending). */
export function nearestIndex(positions: number[], x: number): number {
  const n = positions.length;
  if (n === 0) return 0;
  if (x <= positions[0]) return 0;
  if (x >= positions[n - 1]) return n - 1;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (positions[mid] <= x) lo = mid;
    else hi = mid;
  }
  return x - positions[lo] <= positions[hi] - x ? lo : hi;
}

export function normalizeSelection(
  a: number,
  b: number,
): [number, number] {
  return a <= b ? [a, b] : [b, a];
}

/**
 * Time gained or lost by the comparison lap inside a section:
 * `delta(end) - delta(start)`. Positive means the comparison lap lost time
 * through the section; this is distinct from the cumulative gap at `end`.
 */
export function sectionDeltaSeconds(
  delta: number[],
  start: number,
  end: number,
): number {
  const [s, e] = normalizeSelection(start, end);
  return (delta[e] ?? 0) - (delta[s] ?? 0);
}

/** `+0.345 s`, `−0.120 s`, `±0.000 s`. */
export function formatSignedSeconds(seconds: number, digits = 3): string {
  const rounded = Number(seconds.toFixed(digits));
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "±";
  return `${sign}${Math.abs(rounded).toFixed(digits)} s`;
}

/** Elapsed lap time `m:ss.mmm` from seconds. */
export function formatElapsedSeconds(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = safe - minutes * 60;
  return `${minutes}:${rest.toFixed(3).padStart(6, "0")}`;
}

export function formatMetres(metres: number): string {
  return `${Math.round(metres)} m`;
}

/**
 * Padded [min, max] over the non-null values of several series; `null` when
 * nothing is recorded. A flat series gets a ±1 band so it still draws.
 */
export function valueDomain(
  series: Array<(number | null)[]>,
  options: { includeZero?: boolean; padding?: number } = {},
): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  for (const values of series) {
    for (const v of values) {
      if (v === null || !Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (min === Infinity) return null;
  if (options.includeZero) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  if (max - min < 1e-9) {
    return [min - 1, max + 1];
  }
  const pad = (max - min) * (options.padding ?? 0.08);
  // Quantities that never go negative (pressure, speed, rpm) keep a zero
  // floor instead of a padded negative axis label.
  const lower = min >= 0 ? Math.max(0, min - pad) : min - pad;
  return [lower, max + pad];
}

/** Channels to show when the URL carries none: speed when both laps have it. */
export function defaultChannelKeys(channels: LapComparisonChannel[]): string[] {
  const shared = channels.filter((c) => c.availability === "both");
  const speed = shared.find((c) => c.key === DEFAULT_CHANNEL_KEY);
  if (speed) return [speed.key];
  if (shared[0]) return [shared[0].key];
  return channels[0] ? [channels[0].key] : [];
}

/** Keep only keys the response knows about, in the user's order. */
export function sanitizeChannelKeys(
  keys: string[],
  channels: LapComparisonChannel[],
): string[] {
  const known = new Set(channels.map((c) => c.key));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of keys) {
    if (known.has(key) && !seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out.slice(0, MAX_COMPARISON_CHANNELS);
}

/** "Nice" tick spacing for a metre axis of the given length. */
export function positionTickStep(lengthM: number, targetTicks = 8): number {
  const raw = lengthM / targetTicks;
  const steps = [50, 100, 200, 250, 500, 1000, 2000, 5000];
  return steps.find((s) => s >= raw) ?? 5000;
}

/** Nickname when set, otherwise "year maker model". */
export function vehicleLabel(vehicle: {
  name: string | null;
  year: number;
  maker: string;
  model: string;
}): string {
  return vehicle.name?.trim() || `${vehicle.year} ${vehicle.maker} ${vehicle.model}`;
}

/**
 * Lenient lap-time input: "1:55", "01:55.5", "1:55.123", or plain seconds
 * ("115", "115.5"). Fractions are right-padded to milliseconds. Returns
 * null for anything else (including 0).
 */
export function parseLapTimeInput(value: string): number | null {
  const text = value.trim();
  if (!text) return null;
  const match = /^(?:(\d{1,3}):)?(\d{1,2}|\d{1,5})(?:\.(\d{1,3}))?$/.exec(text);
  if (!match) return null;
  const minutes = match[1] !== undefined ? Number(match[1]) : 0;
  const seconds = Number(match[2]);
  if (match[1] !== undefined && seconds >= 60) return null;
  const millis = match[3] !== undefined ? Number(match[3].padEnd(3, "0")) : 0;
  const total = (minutes * 60 + seconds) * 1000 + millis;
  return total > 0 ? total : null;
}

/** `YYYY-MM-DD` from a date input → ISO bounds in the viewer's timezone. */
export function dateInputToIso(
  value: string,
  edge: "start" | "end",
): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T${edge === "start" ? "00:00:00" : "23:59:59.999"}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
